import * as React from "react";
import { SidebarProvider, useSidebar } from "../../../components/ui/sidebar";

const STORAGE_KEY = "ui.sidebar.open";

function usePersistentSidebar(defaultOpen = true) {
  const [open, setOpen] = React.useState(defaultOpen);

  // init from storage or breakpoint
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored !== null) {
      setOpen(stored === "true");
      return;
    }
    // first run: default open on md+, closed on small screens
    const isSmall = window.matchMedia("(max-width: 767px)").matches;
    setOpen(!isSmall);
  }, []);

  // persist on change
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(STORAGE_KEY, String(open));
  }, [open]);

  return { open, setOpen };
}

// Optional bridge so shadcn's context toggling updates our state too
function Bridge() {
  const { open, setOpen } = React.useContext(
    React.createContext({ open: true, setOpen: (_: boolean) => {} }),
  ); // placeholder to satisfy ts if needed
  const sidebar = useSidebar();
  // keep shadcn context in sync if you expose a toggle from inside
  React.useEffect(() => {
    if (sidebar.open !== open) sidebar.setOpen(open);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  React.useEffect(() => {
    setOpen(sidebar.open);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sidebar.open]);
  return null;
}

export function PersistentSidebarProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { open, setOpen } = usePersistentSidebar(true);
  return (
    <SidebarProvider open={open} onOpenChange={setOpen}>
      {/* If you don't need two-way sync, remove <Bridge/> */}
      {children}
    </SidebarProvider>
  );
}
